import { useState, useRef, useEffect, useCallback } from 'react';
import { Gauge, LayoutDashboard, Trophy, Users, LineChart, MapPin, Settings, Package, ChevronDown, Database, BarChart2, Circle, GitCompareArrows, Building2, Upload, Wrench, Layers, GraduationCap, Eye, CornerUpRight, History, Disc, Loader2, CheckCircle2, LogOut } from 'lucide-react';
import { useRouter } from '@/context/RouterContext';
import { useI18n } from '@/context/I18nContext';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/context/ToastContext';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';
import { TelemetryPage } from '@/types/routes';
import { rpc } from '@/api/client';
import type { LapAnalysisQueueStatusResponse } from '@/types/telemetry';

const RACE_PAGES = new Set<TelemetryPage>([
  TelemetryPage.Races,
  TelemetryPage.RaceSeason,
  TelemetryPage.RaceEvent,
  TelemetryPage.RaceEventCategory,
  TelemetryPage.RaceDriver,
]);

const DRIVER_PAGES = new Set<TelemetryPage>([
  TelemetryPage.RaceDrivers,
  TelemetryPage.RaceDriver,
  TelemetryPage.RaceDriverPace,
  TelemetryPage.RaceDriverStats,
]);

const ANALYSIS_PAGES = new Set<TelemetryPage>([
  TelemetryPage.CornerAnalysis,
  TelemetryPage.BrakingAnalysis,
  TelemetryPage.SectorAnalysis,
  TelemetryPage.TyreAnalysis,
  TelemetryPage.TyreOverview,
  TelemetryPage.LapDiffAnalysis,
  TelemetryPage.AggregationAnalysis,
  TelemetryPage.AggregationDiffAnalysis,
  TelemetryPage.SetupCoach,
  TelemetryPage.AnalysisHistory,
]);

const TRACK_PAGES = new Set<TelemetryPage>([
  TelemetryPage.Tracks,
  TelemetryPage.TrackDetail,
]);

const INVENTORY_PAGES = new Set<TelemetryPage>([
  TelemetryPage.Inventory,
]);

const TEAM_PAGES = new Set<TelemetryPage>([
  TelemetryPage.Teams,
  TelemetryPage.TeamDetail,
]);

const SESSION_PAGES = new Set<TelemetryPage>([
  TelemetryPage.Dashboard,
  TelemetryPage.Session,
  TelemetryPage.Lap,
]);

/** Pages that fall under the "Organization" dropdown */
const ORGANIZATION_PAGES = new Set<TelemetryPage>([
  ...INVENTORY_PAGES,
  ...TEAM_PAGES,
]);

const SETUP_PAGES = new Set<TelemetryPage>([
  TelemetryPage.Setup,
]);

/** Pages that fall under the "Data" dropdown */
const DATA_PAGES = new Set<TelemetryPage>([
  ...SESSION_PAGES,
  ...TRACK_PAGES,
  ...RACE_PAGES,
  ...DRIVER_PAGES,
  ...SETUP_PAGES,
  TelemetryPage.Upload,
]);

export function Header() {
  const { view, navigate } = useRouter();
  const { t } = useI18n();
  const { signOut } = useAuth();
  const [dataOpen, setDataOpen] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(false);
  const [orgOpen, setOrgOpen] = useState(false);
  const headerRef = useRef<HTMLElement>(null);

  const [queueStatus, setQueueStatus] = useState<LapAnalysisQueueStatusResponse | null>(null);
  const [queueVisible, setQueueVisible] = useState(false);
  const wasActiveRef = useRef(false);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const checkQueueStatus = useCallback(async () => {
    try {
      const res = await rpc<LapAnalysisQueueStatusResponse>('GetLapAnalysisQueueStatus', {});
      const total = res.totalEnqueued ?? 0;
      const completed = res.completedCount ?? 0;
      const failed = res.failedCount ?? 0;
      const pending = res.pendingCount ?? Math.max(0, total - completed - failed);
      const active = Boolean(res.isActive && total > 0 && pending > 0);

      setQueueStatus({
        totalEnqueued: total,
        completedCount: completed,
        failedCount: failed,
        pendingCount: pending,
        isActive: active,
      });

      if (active) {
        if (hideTimerRef.current) {
          clearTimeout(hideTimerRef.current);
          hideTimerRef.current = null;
        }
        wasActiveRef.current = true;
        setQueueVisible(true);
      } else if (wasActiveRef.current && total > 0) {
        wasActiveRef.current = false;
        setQueueVisible(true);
        window.dispatchEvent(new CustomEvent('queue:completed'));
        if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
        hideTimerRef.current = setTimeout(() => {
          setQueueVisible(false);
        }, 3000);
      }
    } catch {
      // Ignore transient network errors during background queue status poll
    }
  }, []);

  useEffect(() => {
    checkQueueStatus();
    const onQueueUpdated = () => {
      wasActiveRef.current = true;
      checkQueueStatus();
    };
    const onFocusOrVisible = () => {
      if (document.visibilityState === 'visible') {
        checkQueueStatus();
      }
    };
    window.addEventListener('queue:updated', onQueueUpdated);
    window.addEventListener('focus', onFocusOrVisible);
    document.addEventListener('visibilitychange', onFocusOrVisible);
    return () => {
      window.removeEventListener('queue:updated', onQueueUpdated);
      window.removeEventListener('focus', onFocusOrVisible);
      document.removeEventListener('visibilitychange', onFocusOrVisible);
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    };
  }, [checkQueueStatus]);

  useEffect(() => {
    const intervalMs = queueStatus?.isActive ? 10000 : 60000;
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        checkQueueStatus();
      }
    }, intervalMs);
    return () => clearInterval(interval);
  }, [queueStatus?.isActive, checkQueueStatus]);

  const queueTotal = queueStatus?.totalEnqueued ?? 0;
  const queueDone = Math.min(queueTotal, (queueStatus?.completedCount ?? 0) + (queueStatus?.failedCount ?? 0));
  const queuePct = queueTotal > 0 ? Math.min(100, Math.round((queueDone / queueTotal) * 100)) : 0;
  const queueCompleted = queueTotal > 0 && !queueStatus?.isActive && queueDone >= queueTotal;

  const isAnyMenuOpen = dataOpen || toolsOpen || orgOpen;

  const closeAll = () => {
    setDataOpen(false);
    setToolsOpen(false);
    setOrgOpen(false);
  };

  // Close dropdowns on route change
  useEffect(() => {
    closeAll();
  }, [view]);

  // Lock background scrolling while any mobile dropdown menu is open
  useEffect(() => {
    if (!isAnyMenuOpen) return;
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;

    const mql = window.matchMedia('(max-width: 639px)');
    const originalOverflow = document.body.style.overflow;
    let locked = false;

    const updateScrollLock = () => {
      if (mql.matches) {
        if (!locked) {
          document.body.style.overflow = 'hidden';
          document.body.classList.add('modal-open');
          locked = true;
        }
      } else if (locked) {
        document.body.style.overflow = originalOverflow;
        document.body.classList.remove('modal-open');
        locked = false;
      }
    };

    updateScrollLock();
    mql.addEventListener('change', updateScrollLock);

    return () => {
      mql.removeEventListener('change', updateScrollLock);
      if (locked) {
        document.body.style.overflow = originalOverflow;
        document.body.classList.remove('modal-open');
      }
    };
  }, [isAnyMenuOpen]);

  // Close dropdowns on outside click / tap
  useEffect(() => {
    if (!isAnyMenuOpen) return;
    const handler = (e: MouseEvent | TouchEvent) => {
      if (headerRef.current && !headerRef.current.contains(e.target as Node)) {
        closeAll();
      }
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [isAnyMenuOpen]);

  // Close dropdowns on escape
  useEffect(() => {
    if (!isAnyMenuOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeAll();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isAnyMenuOpen]);

  const isSettings = view === TelemetryPage.Settings;
  const isSetup = view === TelemetryPage.Setup;
  const isUpload = view === TelemetryPage.Upload;
  const isData = DATA_PAGES.has(view);
  const isHome = SESSION_PAGES.has(view);
  const isRaces = RACE_PAGES.has(view) && view !== TelemetryPage.RaceDriver && view !== TelemetryPage.RaceDriverPace;
  const isDrivers = DRIVER_PAGES.has(view);
  const isTracks = TRACK_PAGES.has(view);
  const isTools = ANALYSIS_PAGES.has(view);
  const isInventory = INVENTORY_PAGES.has(view);
  const isTeams = TEAM_PAGES.has(view);
  const isOrg = ORGANIZATION_PAGES.has(view);

  const dataItems: Array<{
    label: string;
    icon: typeof LayoutDashboard;
    href?: string;
    onClick?: () => void;
    active: boolean;
  }> = [
    { label: t('nav.home'), icon: LayoutDashboard, href: '#/', active: isHome },
    { label: t('nav.tracks'), icon: MapPin, href: '#/tracks', active: isTracks },
    { label: t('nav.races'), icon: Trophy, href: '#/races', active: isRaces },
    { label: t('nav.drivers'), icon: Users, href: '#/drivers', active: isDrivers },
    { label: t('setup.pageTitle'), icon: Wrench, href: '#/setup', active: isSetup },
    { label: t('settings.uploadSessions'), icon: Upload, href: '#/upload', active: isUpload },
  ];

  const toolItems: Array<{
    label: string;
    icon: typeof CornerUpRight;
    href?: string;
    onClick?: () => void;
    active: boolean;
  }> = [
    { label: t('analysis.corner.title'), icon: CornerUpRight, href: '#/analysis/corner', active: view === TelemetryPage.CornerAnalysis },
    { label: t('analysis.braking.title'), icon: Disc, href: '#/analysis/braking', active: view === TelemetryPage.BrakingAnalysis },
    { label: t('analysis.sector.title'), icon: BarChart2, href: '#/analysis/sector', active: view === TelemetryPage.SectorAnalysis },
    { label: t('analysis.tyre.title'), icon: Circle, href: '#/analysis/tyre', active: view === TelemetryPage.TyreAnalysis },
    { label: t('analysis.tyreOverview.title'), icon: Eye, href: '#/analysis/tyre-overview', active: view === TelemetryPage.TyreOverview },
    { label: t('analysis.aggregation.title'), icon: Layers, href: '#/analysis/aggregation', active: view === TelemetryPage.AggregationAnalysis },
    { label: t('analysis.aggregationdiff.title'), icon: GitCompareArrows, href: '#/analysis/aggregation-diff', active: view === TelemetryPage.AggregationDiffAnalysis || view === TelemetryPage.LapDiffAnalysis },
    { label: t('coach.title'), icon: GraduationCap, href: '#/analysis/setup-coach', active: view === TelemetryPage.SetupCoach },
    { label: t('analysis.history.title'), icon: History, href: '#/analysis/history', active: view === TelemetryPage.AnalysisHistory },
  ];

  const toggleData = () => {
    setDataOpen(o => !o); setToolsOpen(false); setOrgOpen(false);
  };
  const toggleTools = () => {
    setToolsOpen(o => !o); setDataOpen(false); setOrgOpen(false);
  };
  const toggleOrg = () => {
    setOrgOpen(o => !o); setDataOpen(false); setToolsOpen(false);
  };

  const orgItems = [
    { label: t('nav.inventory'), icon: Package, href: '#/inventory', active: isInventory },
    { label: t('nav.teams'), icon: Users, href: '#/teams', active: isTeams },
  ];

  return (
    <header ref={headerRef} className="fixed top-0 left-0 right-0 z-50">
      {/* Main navbar */}
      <div className="nav-bar h-[60px] sm:h-[52px]">
        <div className="h-full max-w-[960px] mx-auto px-5 sm:px-8 flex items-center">
          <a
            href="#/"
            onClick={closeAll}
            className="flex items-center gap-2.5 text-text-primary"
          >
            <Gauge size={18} className="text-accent" />
            <span className="text-sm font-semibold tracking-tight">kart.win</span>
          </a>

          <nav className="flex items-center gap-1 ml-4 sm:ml-8">
              {/* Data trigger */}
              <button
                onClick={toggleData}
                title={t('nav.data')}
                className={`
                  flex items-center gap-1.5 px-2 sm:px-3 py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer
                  ${isData || dataOpen ? 'text-accent bg-accent/10' : 'text-text-muted hover:text-text-primary hover:bg-bg-hover'}
                `}
              >
                <Database size={14} />
                <span className="hidden sm:inline">{t('nav.data')}</span>
                <ChevronDown size={12} className={`transition-transform duration-200 ${dataOpen ? 'rotate-180' : ''}`} />
              </button>

              {/* Tools trigger */}
              <button
                onClick={toggleTools}
                title={t('nav.tools')}
                className={`
                  flex items-center gap-1.5 px-2 sm:px-3 py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer
                  ${isTools || toolsOpen ? 'text-accent bg-accent/10' : 'text-text-muted hover:text-text-primary hover:bg-bg-hover'}
                `}
              >
                <LineChart size={14} />
                <span className="hidden sm:inline">{t('nav.tools')}</span>
                <ChevronDown size={12} className={`transition-transform duration-200 ${toolsOpen ? 'rotate-180' : ''}`} />
              </button>

              {/* Organization trigger */}
              <button
                onClick={toggleOrg}
                title={t('nav.organization')}
                className={`
                  flex items-center gap-1.5 px-2 sm:px-3 py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer
                  ${isOrg || orgOpen ? 'text-accent bg-accent/10' : 'text-text-muted hover:text-text-primary hover:bg-bg-hover'}
                `}
              >
                <Building2 size={14} />
                <span className="hidden sm:inline">{t('nav.organization')}</span>
                <ChevronDown size={12} className={`transition-transform duration-200 ${orgOpen ? 'rotate-180' : ''}`} />
              </button>
          </nav>

          <div className="flex items-center gap-1 ml-auto">
            {queueVisible && queueTotal > 0 && (
              <div
                data-testid="lap-analysis-queue-progress"
                className="hidden sm:flex items-center gap-2 mr-1 px-2.5 py-1 rounded-md bg-bg-raised border border-border-subtle text-xs text-text-secondary"
                title={
                  queueCompleted
                    ? t('queue.completed', { total: String(queueTotal) })
                    : t('queue.processingLaps', { done: String(queueDone), total: String(queueTotal) })
                }
              >
                {queueCompleted ? (
                  <CheckCircle2 size={13} className="text-emerald-400 shrink-0" />
                ) : (
                  <Loader2 size={13} className="text-accent animate-spin shrink-0" />
                )}
                <span className="tabular-nums font-medium text-[0.72rem] whitespace-nowrap">
                  {queueCompleted
                    ? t('queue.completed', { total: String(queueTotal) })
                    : t('queue.processingLaps', { done: String(queueDone), total: String(queueTotal) })}
                </span>
                <div className="w-16 h-1.5 rounded-full bg-bg-surface overflow-hidden">
                  <div
                    className={`h-full transition-all duration-300 ${queueCompleted ? 'bg-emerald-400' : 'bg-accent'}`}
                    style={{ width: `${queuePct}%` }}
                  />
                </div>
              </div>
            )}
            <button
              onClick={() => {
                navigate('#/settings');
                closeAll();
              }}
              title={t('nav.settings')}
              aria-label={t('nav.settings')}
              className={`flex items-center gap-1.5 px-2 py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer
                ${isSettings ? 'text-accent bg-accent/10' : 'text-text-muted hover:text-text-primary hover:bg-bg-hover'}`}
            >
              <Settings size={14} />
            </button>
            <button
              onClick={() => {
                closeAll();
                signOut();
              }}
              title={t('settings.signOut')}
              aria-label={t('settings.signOut')}
              className="flex items-center gap-1.5 px-2 py-1.5 rounded-md text-xs font-medium text-text-muted hover:text-text-primary hover:bg-bg-hover transition-colors cursor-pointer"
            >
              <LogOut size={14} />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile queue progress bar */}
      {queueVisible && queueTotal > 0 && (
        <div className="sm:hidden bg-bg-raised/95 border-b border-border-subtle px-5 py-1.5 flex items-center justify-between gap-3 backdrop-blur-md">
          <div className="flex items-center gap-1.5 text-[0.72rem] text-text-secondary font-medium truncate">
            {queueCompleted ? (
              <CheckCircle2 size={12} className="text-emerald-400 shrink-0" />
            ) : (
              <Loader2 size={12} className="text-accent animate-spin shrink-0" />
            )}
            <span className="tabular-nums truncate">
              {queueCompleted
                ? t('queue.completed', { total: String(queueTotal) })
                : t('queue.processingLaps', { done: String(queueDone), total: String(queueTotal) })}
            </span>
          </div>
          <div className="w-24 h-1.5 rounded-full bg-bg-surface overflow-hidden shrink-0">
            <div
              className={`h-full transition-all duration-300 ${queueCompleted ? 'bg-emerald-400' : 'bg-accent'}`}
              style={{ width: `${queuePct}%` }}
            />
          </div>
        </div>
      )}

      {/* Dimming Backdrop Overlay on mobile (starts below the navbar at 60px) */}
      {isAnyMenuOpen && (
        <div
          className="fixed inset-x-0 top-[60px] sm:top-[52px] bottom-0 z-40 bg-black/50 sm:hidden transition-opacity"
          style={{
            animation: 'menu-backdrop-fade 200ms cubic-bezier(0.16, 1, 0.3, 1) both',
          }}
          onClick={closeAll}
          aria-hidden="true"
        />
      )}

      {/* Slide-down Data Panel */}
      {dataOpen && (
        <div
          className="
            fixed top-[66px] sm:top-[58px] inset-x-3 z-50 max-h-[calc(100dvh-80px)] overflow-y-auto
            bg-bg-raised/95 border border-border-default rounded-xl shadow-2xl shadow-black/60 backdrop-blur-xl p-2
            sm:static sm:inset-auto sm:top-auto sm:max-h-none sm:overflow-visible sm:rounded-none sm:shadow-none sm:border-0 sm:border-b sm:border-border-subtle sm:p-0
          "
          style={{
            animation: 'data-panel-in 180ms ease-out both',
          }}
        >
          <div className="max-w-[960px] mx-auto sm:px-8 sm:py-1.5">
            <div className="flex flex-col gap-1 sm:grid sm:grid-cols-3 sm:gap-1.5">
              {dataItems.map((item, i) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.href || item.label}
                    onClick={() => {
                      if (item.onClick) {
                        item.onClick();
                      } else if (item.href) {
                        navigate(item.href);
                      }
                      closeAll();
                    }}
                    className={`
                      flex items-center gap-3 sm:gap-2.5 w-full min-h-[48px] sm:min-h-0 px-4 py-3 sm:px-3 sm:py-2 rounded-lg sm:rounded-md
                      text-sm sm:text-[0.8rem] font-medium
                      transition-all duration-150 cursor-pointer text-left
                      ${item.active
                        ? 'text-accent bg-accent/10'
                        : 'text-text-secondary hover:text-text-primary hover:bg-white/[0.05] active:bg-white/[0.08]'
                      }
                    `}
                    style={{
                      animation: `data-item-in 180ms ease-out ${60 + i * 30}ms both`,
                    }}
                  >
                    <Icon size={18} className="w-[18px] h-[18px] sm:w-3.5 sm:h-3.5 flex-shrink-0" />
                    <span className="flex-1 truncate">{item.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Slide-down Tools Panel */}
      {toolsOpen && (
        <div
          className="
            fixed top-[66px] sm:top-[58px] inset-x-3 z-50 max-h-[calc(100dvh-80px)] overflow-y-auto
            bg-bg-raised/95 border border-border-default rounded-xl shadow-2xl shadow-black/60 backdrop-blur-xl p-2
            sm:static sm:inset-auto sm:top-auto sm:max-h-none sm:overflow-visible sm:rounded-none sm:shadow-none sm:border-0 sm:border-b sm:border-border-subtle sm:p-0
          "
          style={{
            animation: 'data-panel-in 180ms ease-out both',
          }}
        >
          <div className="max-w-[960px] mx-auto sm:px-8 sm:py-1.5">
            <div className="flex flex-col gap-1 sm:grid sm:grid-cols-3 sm:gap-1.5">
              {toolItems.map((item, i) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.href || item.label}
                    onClick={() => {
                      if (item.onClick) {
                        item.onClick();
                      } else if (item.href) {
                        navigate(item.href);
                      }
                      closeAll();
                    }}
                    className={`
                      flex items-center gap-3 sm:gap-2.5 w-full min-h-[48px] sm:min-h-0 px-4 py-3 sm:px-3 sm:py-2 rounded-lg sm:rounded-md
                      text-sm sm:text-[0.8rem] font-medium
                      transition-all duration-150 cursor-pointer text-left
                      ${item.active
                        ? 'text-accent bg-accent/10'
                        : 'text-text-secondary hover:text-text-primary hover:bg-white/[0.05] active:bg-white/[0.08]'
                      }
                    `}
                    style={{
                      animation: `data-item-in 180ms ease-out ${60 + i * 25}ms both`,
                    }}
                  >
                    <Icon size={18} className="w-[18px] h-[18px] sm:w-3.5 sm:h-3.5 flex-shrink-0" />
                    <span className="flex-1 truncate">{item.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Slide-down Organization Panel */}
      {orgOpen && (
        <div
          className="
            fixed top-[66px] sm:top-[58px] inset-x-3 z-50 max-h-[calc(100dvh-80px)] overflow-y-auto
            bg-bg-raised/95 border border-border-default rounded-xl shadow-2xl shadow-black/60 backdrop-blur-xl p-2
            sm:static sm:inset-auto sm:top-auto sm:max-h-none sm:overflow-visible sm:rounded-none sm:shadow-none sm:border-0 sm:border-b sm:border-border-subtle sm:p-0
          "
          style={{
            animation: 'data-panel-in 180ms ease-out both',
          }}
        >
          <div className="max-w-[960px] mx-auto sm:px-8 sm:py-1.5">
            <div className="flex flex-col gap-1 sm:grid sm:grid-cols-2 sm:gap-1.5">
              {orgItems.map((item, i) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.href}
                    onClick={() => { navigate(item.href); closeAll(); }}
                    className={`
                      flex items-center gap-3 sm:gap-2.5 w-full min-h-[48px] sm:min-h-0 px-4 py-3 sm:px-3 sm:py-2 rounded-lg sm:rounded-md
                      text-sm sm:text-[0.8rem] font-medium
                      transition-all duration-150 cursor-pointer text-left
                      ${item.active
                        ? 'text-accent bg-accent/10'
                        : 'text-text-secondary hover:text-text-primary hover:bg-white/[0.05] active:bg-white/[0.08]'
                      }
                    `}
                    style={{
                      animation: `data-item-in 180ms ease-out ${60 + i * 30}ms both`,
                    }}
                  >
                    <Icon size={18} className="w-[18px] h-[18px] sm:w-3.5 sm:h-3.5 flex-shrink-0" />
                    <span className="flex-1 truncate">{item.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
